/**
 * pdfCache — session + localStorage cache for generated report PDFs.
 * Ported from platform-2.0's lib/pdfCache so the practice apps behave
 * exactly like the in-game Game Library: generate once, then the kebab
 * offers Preview / Download / Regenerate against the cached copy.
 *
 * Entries live 24h, capped at 20 PDFs (localStorage quota is ~5MB and
 * report PDFs run ~100-500KB as base64). Quota errors degrade gracefully:
 * the in-memory cache still works for the session.
 */

export interface CachedPDF {
  blob: Blob;
  url: string;
  fileName: string;
  gameId: number;
  timestamp: number;
  gameName: string;
}

interface StoredPDF {
  fileName: string;
  gameId: number;
  timestamp: number;
  gameName: string;
  /** Base64-encoded blob bytes. */
  blobData: string;
}

const MAX_AGE_MS = 24 * 60 * 60 * 1000;
const MAX_ENTRIES = 20;
const STORAGE_KEY = "practice_pdf_cache";

function cacheKey(gameId: number, gameName: string): string {
  return `${gameId}-${gameName.replace(/[^a-zA-Z0-9]/g, "_")}`;
}

async function blobToBase64(blob: Blob): Promise<string> {
  const buf = new Uint8Array(await blob.arrayBuffer());
  // Chunked to avoid call-stack limits on large PDFs.
  let binary = "";
  const CHUNK = 0x8000;
  for (let i = 0; i < buf.length; i += CHUNK) {
    binary += String.fromCharCode(...buf.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

function base64ToBlob(data: string): Blob {
  const binary = atob(data);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: "application/pdf" });
}

class PdfCache {
  private cache = new Map<string, CachedPDF>();

  constructor() {
    this.loadFromStorage();
  }

  private loadFromStorage(): void {
    if (typeof localStorage === "undefined") return;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const stored: Record<string, StoredPDF> = JSON.parse(raw);
      for (const [key, entry] of Object.entries(stored)) {
        if (Date.now() - entry.timestamp > MAX_AGE_MS) continue;
        const blob = base64ToBlob(entry.blobData);
        this.cache.set(key, {
          blob,
          url: URL.createObjectURL(blob),
          fileName: entry.fileName,
          gameId: entry.gameId,
          timestamp: entry.timestamp,
          gameName: entry.gameName,
        });
      }
    } catch (err) {
      console.warn("[pdfCache] Failed to load cached PDFs", err);
    }
  }

  /**
   * Serialize the whole cache once (unlike the platform original, which
   * fired one racy FileReader per entry and re-wrote the key N times).
   */
  private async saveToStorage(): Promise<void> {
    if (typeof localStorage === "undefined") return;
    try {
      const stored: Record<string, StoredPDF> = {};
      for (const [key, pdf] of this.cache.entries()) {
        stored[key] = {
          fileName: pdf.fileName,
          gameId: pdf.gameId,
          timestamp: pdf.timestamp,
          gameName: pdf.gameName,
          blobData: await blobToBase64(pdf.blob),
        };
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
    } catch (err) {
      // Quota exceeded etc. — session cache still works.
      console.warn("[pdfCache] Failed to persist cached PDFs", err);
    }
  }

  private cleanup(): void {
    const now = Date.now();
    for (const [key, pdf] of [...this.cache.entries()]) {
      if (now - pdf.timestamp > MAX_AGE_MS) {
        URL.revokeObjectURL(pdf.url);
        this.cache.delete(key);
      }
    }
    if (this.cache.size >= MAX_ENTRIES) {
      const oldestFirst = [...this.cache.entries()].sort(
        ([, a], [, b]) => a.timestamp - b.timestamp,
      );
      for (const [key, pdf] of oldestFirst.slice(
        0,
        this.cache.size - MAX_ENTRIES + 1,
      )) {
        URL.revokeObjectURL(pdf.url);
        this.cache.delete(key);
      }
    }
  }

  cachePDF(gameId: number, gameName: string, blob: Blob): CachedPDF {
    this.cleanup();
    const key = cacheKey(gameId, gameName);
    const existing = this.cache.get(key);
    if (existing) URL.revokeObjectURL(existing.url);

    const entry: CachedPDF = {
      blob,
      url: URL.createObjectURL(blob),
      fileName: `${gameName}-qb-report.pdf`,
      gameId,
      timestamp: Date.now(),
      gameName,
    };
    this.cache.set(key, entry);
    void this.saveToStorage();
    return entry;
  }

  getCachedPDF(gameId: number, gameName: string): CachedPDF | null {
    const entry = this.cache.get(cacheKey(gameId, gameName));
    if (!entry) return null;
    if (Date.now() - entry.timestamp > MAX_AGE_MS) {
      URL.revokeObjectURL(entry.url);
      this.cache.delete(cacheKey(gameId, gameName));
      void this.saveToStorage();
      return null;
    }
    return entry;
  }

  hasCachedPDF(gameId: number, gameName: string): boolean {
    return this.getCachedPDF(gameId, gameName) !== null;
  }

  /** Returns false when the PDF is no longer cached (caller shows an error). */
  downloadPDF(gameId: number, gameName: string): boolean {
    const entry = this.getCachedPDF(gameId, gameName);
    if (!entry) return false;
    const a = document.createElement("a");
    a.href = entry.url;
    a.download = entry.fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    return true;
  }
}

export const pdfCache = new PdfCache();
